import { useEffect, useMemo, useState } from 'react';
import { getImageUrl, getWatchProviders, type WatchProviderRegion } from '../../services/tmdb';

interface WhereToWatchProps {
  mediaType: 'movie' | 'tv';
  mediaId: number;
  region?: string;
}

const CATEGORY_LABELS: Array<[keyof Pick<WatchProviderRegion, 'flatrate' | 'free' | 'ads' | 'rent' | 'buy'>, string]> = [
  ['flatrate', 'Stream'],
  ['free', 'Free'],
  ['ads', 'With ads'],
  ['rent', 'Rent'],
  ['buy', 'Buy'],
];

export default function WhereToWatch({ mediaType, mediaId, region = 'US' }: WhereToWatchProps) {
  const [providerRegion, setProviderRegion] = useState<WatchProviderRegion | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadProviders = async () => {
      try {
        setLoading(true);
        const response = await getWatchProviders(mediaType, mediaId);
        if (!cancelled) setProviderRegion(response.results[region] ?? null);
      } catch (error) {
        console.error('Unable to load watch providers:', error);
        if (!cancelled) setProviderRegion(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProviders();
    return () => {
      cancelled = true;
    };
  }, [mediaId, mediaType, region]);

  const groups = useMemo(
    () => CATEGORY_LABELS.flatMap(([key, label]) => {
      const providers = providerRegion?.[key] ?? [];
      return providers.length > 0 ? [{ key, label, providers }] : [];
    }),
    [providerRegion],
  );

  if (loading) {
    return (
      <div className="mt-6 h-20 max-w-3xl animate-pulse rounded-2xl border border-white/10 bg-white/[0.04]" aria-label="Loading watch providers" />
    );
  }

  if (!providerRegion || groups.length === 0) return null;

  return (
    <section className="mt-6 max-w-3xl rounded-2xl border border-emerald-300/15 bg-black/25 p-4 text-left" aria-labelledby="where-to-watch-title">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p id="where-to-watch-title" className="text-sm font-bold text-white">Where to watch legally</p>
          <p className="mt-1 text-xs text-white/45">Availability for {region}. Provider data by JustWatch via TMDB.</p>
        </div>
        <a
          href={providerRegion.link}
          target="_blank"
          rel="noopener noreferrer"
          referrerPolicy="no-referrer"
          className="text-xs font-semibold text-emerald-200 transition hover:text-emerald-100"
        >
          Check availability ↗
        </a>
      </div>

      <div className="mt-4 flex flex-wrap gap-4">
        {groups.map((group) => (
          <div key={group.key}>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{group.label}</p>
            <div className="flex flex-wrap gap-2">
              {group.providers.slice(0, 6).map((provider) => (
                <a
                  key={`${group.key}-${provider.provider_id}`}
                  href={providerRegion.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  title={`${group.label} with ${provider.provider_name}`}
                  className="group/provider relative overflow-hidden rounded-xl ring-1 ring-white/10 transition hover:-translate-y-0.5 hover:ring-emerald-300/50"
                >
                  <img
                    src={getImageUrl(provider.logo_path, 'w92')}
                    alt={provider.provider_name}
                    className="h-10 w-10 object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
